/**
 * `createShellMount` — the concrete `ShellMount` registry (frontend.md §3.2).
 *
 * A small in-memory registry implementing the `ShellMount` contract. `AppShell` builds one
 * at startup, seeds it with the frontend's own six surfaces, provides it via context, and
 * lets Agent 9's editor/chat register into it.
 *
 * All four registries (routes, nav items, commands, status-bar slots) follow the same
 * shape: a `Map` keyed by a stable id; `register*` upserts and returns an unregister
 * thunk; every mutation notifies subscribers so the shell components re-render. There is
 * no privileged path — the core six and Agent 9 use the identical API.
 */

import type {
  NavItem,
  PaletteCommand,
  RouteRegistration,
  ShellMount,
  StatusBarSlot,
  Unregister,
} from './ShellMount';
import type { ReactNode } from 'react';

interface StatusBarEntry {
  key: string;
  element: ReactNode;
}

export function createShellMount(): ShellMount {
  const routes = new Map<string, RouteRegistration>();
  const navItems = new Map<string, NavItem>();
  const commands = new Map<string, PaletteCommand>();
  const statusBarSlots: Record<StatusBarSlot, Map<string, StatusBarEntry>> = {
    left: new Map(),
    center: new Map(),
    right: new Map(),
  };
  const listeners = new Set<() => void>();

  const notify = (): void => {
    for (const listener of listeners) listener();
  };

  return {
    registerRoute(registration: RouteRegistration): Unregister {
      routes.set(registration.pattern, registration);
      notify();
      return () => {
        routes.delete(registration.pattern);
        notify();
      };
    },

    registerNavItem(item: NavItem): Unregister {
      navItems.set(item.id, item);
      notify();
      return () => {
        navItems.delete(item.id);
        notify();
      };
    },

    registerCommand(command: PaletteCommand): Unregister {
      commands.set(command.id, command);
      notify();
      return () => {
        commands.delete(command.id);
        notify();
      };
    },

    statusBarSlot(slot: StatusBarSlot, key: string, element: ReactNode): Unregister {
      statusBarSlots[slot].set(key, { key, element });
      notify();
      return () => {
        statusBarSlots[slot].delete(key);
        notify();
      };
    },

    getNavItems(): NavItem[] {
      return [...navItems.values()].sort((a, b) => a.order - b.order);
    },

    getCommands(): PaletteCommand[] {
      return [...commands.values()];
    },

    getRoutes(): RouteRegistration[] {
      return [...routes.values()];
    },

    getStatusBarSlot(slot: StatusBarSlot): StatusBarEntry[] {
      return [...statusBarSlots[slot].values()];
    },

    subscribe(listener: () => void): Unregister {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
